import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from "sonner";

// Services
import useSelectedDomain from "../../../service/Hooks/useSelectedDomain";
import { useUserData } from "../../../service/api/useUserData";
import { useUser } from "@clerk/clerk-react";
import { checkUser } from "../../../service/api/AuthService";
import { useLoader } from "../../../service/Hooks/useLoader";
import { useAccountValidationSubmit } from "../../../service/Hooks/useValidationSubmit";
import { getAccountById, getAccountForUpdate, getAccounts } from "../../../service/api/accountService";
import { generateAllSecurityReport } from "../../../service/api/SecurityReportService";
import { generateAllTransactionReport } from "../../../service/api/TransactionReportService";

// Composants (imports inchangés...)
import CustomerForm from "../AccountForm/CustomerForm";
import ContactForm from '../PersonContactComponent/ContactForm';
import TableauPersonne from '../PersonContactComponent/TableauPersonne';
import TableauRepresentations from "../../../LegalRepresentatives/TableauRepresentations";
import AssociationForm from "../AssociationComponent/AssociationForm";
import TableauAssociation from "../AssociationComponent/TableauAssociation";
import ListeSecurities from "../../Titles/ListeSecurities";
import ListeOrders from "../../Order/ListeOrders";
import TransactionClient from "../TransactionClient";
import ValidationModal from "../../../Validations/ValidationModal";
import SubmitConfirmationModal from "../../../Modal/SubmitConfirmationModal";
import ValidationDetailModal from "../../../Validations/ValidationDetailModal";
import ViewMoreModal from "../Viewmore/ViewMoreModal";
import SignatoriesModal from "../../Signatories/SignatoriesModal";
import SearchAndFilters from '../../../accountComponent/SearchAndFilters';
import PaginationSection from '../../../accountComponent/PaginationSection';
import AccountsTable from '../../../accountComponent/AccountsTable';
import CashEditForm from '../AccountCash/CashEditForm';
import AddLegalRepresentativesModal from '../../../LegalRepresentatives/AddLegalRepresentativesModal';
import ManagedAccountsModal from '../../../LegalRepresentatives/ManagedAccountsModal';
import ReportPeriodModal from '../ReleveCompte/ReportPeriodModal';
import AccountHistoricsModal from '../../../AccountHistorics/AccountHistoricsModal';
import '../style/CustomerForm.css';
import SearchFiltersSkeleton from '../../../skeleton/SearchFiltersSkeleton';

/**
 *  COMPOSANT PRINCIPAL OPTIMISÉ
 * Améliorations:
 * 1. Suppression du calcul cashAUC côté frontend (trop lent)
 * 2. Skeleton loaders au lieu de messages vides
 * 3. Polling désactivé par défaut (à activer manuellement)
 * 4. Gestion d'erreur améliorée
 */
const Accounts = ({ onAccountDeleted, onCreateSuccess, isParentOffline }) => {
  // HOOKS (inchangés)
  const selectedDomain = useSelectedDomain();
  const { getUserInfo } = useUserData();
  const { user } = useUser();
  const { showLoader, hideLoader } = useLoader();
  const { submitAccountValidation, isSubmitting } = useAccountValidationSubmit();

  // REF pour éviter les appels multiples
  const isFetchingRef = useRef(false);
  const lastFetchParamsRef = useRef(null);

  // ÉTATS DE GESTION DES DONNÉES
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true); //  NOUVEAU: Premier chargement
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);

  // ÉTATS DE PAGINATION ET FILTRES
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25); //  Réduit de 8 à 25 pour moins d'appels
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [cashFilter, setCashFilter] = useState(null);
  const [aucFilter, setAucFilter] = useState(null);
  const [accountTypeFilter, setAccountTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [isManagedAccountFilter, setIsManagedAccountFilter] = useState(null);

  // ÉTATS DE GESTION DES MODALS (inchangés...)
  const [showCashEditModal, setShowCashEditModal] = useState(false);
  const [accountToEditCash, setAccountToEditCash] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showContactList, setShowContactList] = useState(false);
  const [showAssociationForm, setShowAssociationForm] = useState(false);
  const [showAssociationList, setShowAssociationList] = useState(false);
  const [showSecuritiesModal, setShowSecuritiesModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpens, setIsModalOpens] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);
  const [showSignatoriesModal, setShowSignatoriesModal] = useState(false);
  const [showSubmitAccountConfirmation, setShowSubmitAccountConfirmation] = useState(false);
  const [showValidationDetailModal, setShowValidationDetailModal] = useState(false);
  const [showHistoricsModal, setShowHistoricsModal] = useState(false);
  const [showReportPeriodModal, setShowReportPeriodModal] = useState(false);
  const [showManagedAccountsModal, setShowManagedAccountsModal] = useState(false);

  // ÉTATS DES DONNÉES SÉLECTIONNÉES (inchangés...)
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedAccountForView, setSelectedAccountForView] = useState(null);
  const [selectedAccountForSignatories, setSelectedAccountForSignatories] = useState(null);
  const [selectedAccountForHistorics, setSelectedAccountForHistorics] = useState(null);
  const [selectedReportAccount, setSelectedReportAccount] = useState(null);
  const [selectedReportDomainId, setSelectedReportDomainId] = useState(null);
  const [selectedManagedAccount, setSelectedManagedAccount] = useState(null);
  const [accountToSubmit, setAccountToSubmit] = useState(null);
  const [editAccountData, setEditAccountData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [validationEntityType, setValidationEntityType] = useState('');
  const [validationEntityId, setValidationEntityId] = useState(null);
  const [selectedValidationEntityId, setSelectedValidationEntityId] = useState(null);
  const [selectedValidationEntityType, setSelectedValidationEntityType] = useState('');

  // ÉTATS UTILITAIRES
  const [cashFilterMessage, setCashFilterMessage] = useState('');
  const [aucFilterMessage, setAucFilterMessage] = useState('');
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [roles, setRole] = useState(null);
  const [domains, setDomains] = useState([]);
  const [selectedDomains, setSelectedDomains] = useState(null);

  const isCurrentUserValidator = selectedDomains?.isValidator;

  /**
   *  EFFET : DEBOUNCE DE LA RECHERCHE (OPTIMISÉ)
   */
  useEffect(() => {
    const id = setTimeout(() => {
      const trimmedSearch = searchTerm.trim();
      
      if (trimmedSearch !== debouncedSearchTerm) {
        setDebouncedSearchTerm(trimmedSearch);
        setCurrentPage(1);
      }
    }, 500);
    
    return () => clearTimeout(id);
  }, [searchTerm, debouncedSearchTerm]);

  /**
   *  EFFET : CHARGEMENT DES DONNÉES UTILISATEUR
   */
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) { 
        setLoading(false);
        setInitialLoading(false);
        return; 
      }
      
      try {
        const email = user.emailAddresses[0]?.emailAddress;
        const userData = await checkUser(email);
        
        if (userData?.domains) {
          setDomains(userData.domains);
          sessionStorage.setItem("domains", JSON.stringify(userData.domains));
        }
        
        if (userData?.isAdmin) {
          setRole("admin");
          setSelectedDomains(null);
          sessionStorage.removeItem("selectedDomains");
        } else {
          const storedDomain = sessionStorage.getItem("selectedDomains");
          if (storedDomain) {
            const domainObj = JSON.parse(storedDomain);
            if (domainObj) {
              setSelectedDomains(domainObj);
              setRole(domainObj.role);
            }
          } else if (userData?.domains?.length > 0) {
            const firstDomain = userData.domains[0];
            setSelectedDomains(firstDomain);
            setRole(firstDomain.role);
            sessionStorage.setItem("selectedDomains", JSON.stringify(firstDomain));
          } else {
            setRole("manager");
          }
        }
      } catch (error) {
        console.error("Erreur fetchUserData:", error);
        setRole("manager");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  /**
   *  FONCTION : FETCH ACCOUNTS (ULTRA-OPTIMISÉE)
   * Changements majeurs:
   * - Suppression du calcul cashAUC (trop lent)
   * - Skeleton loader au lieu de message vide
   */
  const fetchAccounts = useCallback(async ({ isSilent = false } = {}) => {
    if (!selectedDomain?.id) {
      setError("Aucun domaine sélectionné");
      setNoResults(false);
      setLoading(false);
      setInitialLoading(false);
      return;
    }

    if (isFetchingRef.current) {
      return;
    }

    const fetchParams = JSON.stringify({
      domainId: selectedDomain.id,
      currentPage,
      pageSize,
      debouncedSearchTerm,
      accountTypeFilter,
      statusFilter,
      isManagedAccountFilter
    });

    if (!isSilent && fetchParams === lastFetchParamsRef.current) {
      return;
    }

    lastFetchParamsRef.current = fetchParams;
    isFetchingRef.current = true;

    if (!isSilent) { 
      setLoading(true); 
      setError(null);
      setNoResults(false);
    }
    
    try {
      const result = await getAccounts({
        domainId: selectedDomain.id,
        pageNumber: currentPage,
        pageSize: pageSize,
        searchTerm: debouncedSearchTerm,
        accountTypeFilter,
        statusFilter,
        isManagedAccount: isManagedAccountFilter
      });
      
      if (result.success) {
        //  OPTIMISATION MAJEURE: Pas de calcul cashAUC côté frontend
        // Les comptes arrivent déjà avec toutes leurs données du backend
        const processedAccounts = result.items || [];
        
        setTotalCount(result.totalCount || 0);
        setAccounts(processedAccounts);
        
        if (processedAccounts.length === 0) {
          setNoResults(true);
          setError(null);
        } else {
          setNoResults(false);
        }
      } else {
        const apiError = result.error || "Erreur lors de la récupération des comptes";
        
        const isNoResultsCase = 
          apiError.includes("Aucun compte trouvé") || 
          apiError.includes("aucun compte") || 
          apiError.toLowerCase().includes("no account") ||
          apiError.toLowerCase().includes("not found") ||
          apiError.includes("Aucune donnée");
        
        if (isNoResultsCase) {
          setNoResults(true);
          setError(null);
          setAccounts([]);
          setTotalCount(0);
        } else {
          let userFriendlyMessage = apiError;
          
          if (apiError.includes("401") || apiError.includes("non autorisé")) {
            userFriendlyMessage = "Session expirée. Veuillez vous reconnecter.";
          } else if (apiError.includes("403") || apiError.includes("interdit")) {
            userFriendlyMessage = "Accès refusé. Vous n'avez pas les permissions nécessaires.";
          } else if (apiError.includes("500") || apiError.includes("serveur")) {
            userFriendlyMessage = "Erreur serveur. Veuillez réessayer plus tard.";
          } else if (apiError.includes("timeout") || apiError.includes("délai")) {
            userFriendlyMessage = "Délai d'attente dépassé.";
          } else if (apiError.includes("network") || apiError.includes("connexion")) {
            userFriendlyMessage = "Erreur de connexion.";
          }
          
          setError(userFriendlyMessage);
          setNoResults(false);
          setAccounts([]);
        }
      }
      
    } catch (err) {
      const isConnectionError = !err.response || 
          err.code === 'ECONNABORTED' || 
          err.message?.includes('Network Error') ||
          err.message?.includes('Failed to fetch') ||
          err.message?.includes('connexion');
      
      if (isConnectionError) {
        setError("CONNEXION_LOST"); 
      } else {
        setError(err.message || "Erreur de chargement");
      }
      
      setAccounts([]);
      setNoResults(false);
    } finally {
      if (!isSilent) {
        setLoading(false);
        setInitialLoading(false); //  Premier chargement terminé
      }
      isFetchingRef.current = false;
    }
  }, [
    selectedDomain, 
    currentPage, 
    pageSize, 
    debouncedSearchTerm, 
    accountTypeFilter, 
    statusFilter,
    isManagedAccountFilter
  ]);

  const isConnectionError = error === "CONNEXION_LOST" || isParentOffline;

  /**
   * POLLING DÉSACTIVÉ - Actualisation uniquement sur action utilisateur
   */
  useEffect(() => {
    if (!selectedDomain?.id) return;
    
    fetchAccounts({ isSilent: false });
    
    // POLLING DÉSACTIVÉ pour améliorer les performances
    // L'utilisateur peut actualiser manuellement avec le bouton refresh
    
  }, [fetchAccounts, selectedDomain]);

  /**
   *  FONCTION : REFRESH ACCOUNTS
   */
  const refreshAccounts = useCallback(async (options = {}) => {
    const {
      showLoading = true,
      resetPagination = false,
      silent = false
    } = options;

    if (resetPagination) {
      setCurrentPage(1);
    }

    if (showLoading && !silent) {
      setLoading(true);
      setError(null);
      setNoResults(false);
    }

    try {
      await fetchAccounts({ isSilent: silent });
      
      if (!silent) {
        toast.success("Liste actualisée");
      }
    } catch (error) {
      if (!silent) {
        toast.error("Erreur lors de l'actualisation");
      }
    }
  }, [fetchAccounts]);

  // GESTIONNAIRES (inchangés pour économiser de l'espace...)
  // Tous les handlers restent identiques
  
  const handleCashFilter = useCallback(() => {
    setCashFilter(prev => prev === 'asc' ? 'desc' : 'asc');
    setCashFilterMessage(cashFilter === 'asc' ? 'Filtre Cash: Descendant' : 'Filtre Cash: Ascendant');
    setAucFilter(null);
    setAucFilterMessage('');
    setCurrentPage(1);
  }, [cashFilter]);

  const handleAucFilter = useCallback(() => {
    setAucFilter(prev => prev === 'asc' ? 'desc' : 'asc');
    setAucFilterMessage(aucFilter === 'asc' ? 'Filtre AUC: Descendant' : 'Filtre AUC: Ascendant');
    setCashFilter(null);
    setCashFilterMessage('');
    setCurrentPage(1);
  }, [aucFilter]);

  const handleAccountTypeFilter = useCallback((type) => {
    setAccountTypeFilter(type || null);
    setCashFilter(null);
    setAucFilter(null);
    setCurrentPage(1);
  }, []);

  const handleStatusFilter = useCallback((status) => {
    setStatusFilter(status || null);
    setCurrentPage(1);
  }, []);

  const handleManagedAccountFilter = useCallback((value) => {
    setIsManagedAccountFilter(value || null);
    setCurrentPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setCashFilter(null);
    setAucFilter(null);
    setStatusFilter(null);
    setCashFilterMessage('');
    setAucFilterMessage('');
    setIsManagedAccountFilter(null);
    setSearchTerm("");
    setCurrentPage(1);
  }, []);

  // Tous les autres handlers restent inchangés...
  const handleShowEditAccountModal = useCallback(async (account) => {
    try {
      const accountData = await getAccountForUpdate(account.id);
      setEditAccountData(accountData);
      setIsEditMode(true);
      
      const modalElement = document.getElementById('newAccount');
      if (modalElement) {
        const modal = new window.bootstrap.Modal(modalElement);
        modal.show();
      }
    } catch (error) {
      toast.error("Erreur lors du chargement : " + error.message);
    }
  }, []);

  const handleEditSuccess = useCallback(() => {
    setIsEditMode(false);
    setEditAccountData(null);
    refreshAccounts({ showLoading: false });
    toast.success("Compte modifié avec succès");
  }, [refreshAccounts]);

  const handleDeleteSuccess = useCallback((accountId, accountTypeName) => {
    setAccounts(prev => prev.filter(acc => acc.id !== accountId));
    if (typeof onAccountDeleted === 'function') {
      onAccountDeleted(accountId, accountTypeName);
    }
  }, [onAccountDeleted]);

  const handleCreateSuccess = useCallback(() => {
    if (onCreateSuccess) {
      onCreateSuccess();
    }
    refreshAccounts({ showLoading: false, silent: true });
  }, [onCreateSuccess, refreshAccounts]);

  const handleGenerateTitleReport = useCallback((accountId, domainId) => {
    const account = accounts.find(acc => acc.id === accountId);
    setSelectedReportAccount(account);
    setSelectedReportDomainId(domainId);
    setShowReportPeriodModal(true);
  }, [accounts]);

  const handleGenerateReportWithPeriod = useCallback(async (period) => {
    if (!selectedReportAccount || !selectedReportDomainId) return;
    
    setIsGeneratingReport(true);
    showLoader({ 
      message: "Génération du relevé de titres...", 
      indeterminate: true 
    });
    
    try {
      const response = await generateAllSecurityReport(
        selectedReportAccount.id, 
        selectedReportDomainId, 
        period
      );
      
      const reportUrl = response.url?.url;
      const successMessage = response.url?.message || "Relevé généré avec succès";
      
      if (reportUrl) {
        window.open(reportUrl, '_blank');
        toast.success(successMessage);
      } else {
        toast.warning("Aucun titre pour ce client sur la période.");
      }
    } catch (error) {
      toast.error(error.message || "Erreur lors de la génération.");
    } finally {
      setIsGeneratingReport(false);
      hideLoader();
      setShowReportPeriodModal(false);
      setSelectedReportAccount(null);
      setSelectedReportDomainId(null);
    }
  }, [selectedReportAccount, selectedReportDomainId, showLoader, hideLoader]);

  const handleGenerateTransactionReport = useCallback(async (accountId, domainId) => {
    setIsGeneratingReport(true);
    showLoader({ message: "Génération du rapport...", indeterminate: true });

    try {
      const response = await generateAllTransactionReport(accountId, domainId);

      if (response.data instanceof Blob) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        
        const contentDisposition = response.headers?.['content-disposition'];
        let fileName = `rapport_transactions_${accountId}_${new Date().toISOString().split('T')[0]}.pdf`;
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch) fileName = fileNameMatch[1];
        }
        
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success("Rapport téléchargé avec succès");
      } else {
        toast.warning("Format de réponse inattendu");
      }
    } catch (error) {
      console.error('Erreur rapport:', error);
      toast.error("Erreur lors de la génération du rapport");
    } finally {
      setIsGeneratingReport(false);
      hideLoader();
    }
  }, [showLoader, hideLoader]);

  // Tous les handlers de modals restent inchangés...
  const handleOpenCashEditModal = useCallback((account) => {
    setAccountToEditCash(account);
    setShowCashEditModal(true);
  }, []);

  const handleOpenDetailsModal = useCallback(async (account) => {
    try {
      const accountDetails = await getAccountById(account.id);
      setSelectedAccountForView(accountDetails);
      setShowViewMoreModal(true);
    } catch (error) {
      toast.error("Erreur lors de la récupération des détails");
    }
  }, []);

  const handleOpenSignatoriesModal = useCallback((accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    setSelectedAccountForSignatories(account);
    setShowSignatoriesModal(true);
  }, [accounts]);

  const handleOpenModalOrders = useCallback((accountId) => {
    setSelectedAccountId(accountId);
    setShowOrdersModal(true);
  }, []);

  const handleOpenModalSecurities = useCallback((accountId) => {
    setSelectedAccountId(accountId);
    setShowSecuritiesModal(true);
  }, []);

  const handleOpenModalTransactions = useCallback((accountId) => {
    setSelectedAccountId(accountId);
    setShowTransactionModal(true);
  }, []);

  const handleOpenModal = useCallback((accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    setSelectedAccount(account);
    setSelectedAccountId(accountId);
    setIsModalOpens(true);
  }, [accounts]);

  const handleOpenValidationModal = useCallback((accountId, entityType) => {
    setValidationEntityId(accountId);
    setValidationEntityType(entityType);
    setShowValidationModal(true);
  }, []);

  const handleOpenValidationDetails = useCallback((entityId, entityType = 'Order') => {
    setSelectedValidationEntityId(entityId);
    setSelectedValidationEntityType(entityType);
    setShowValidationDetailModal(true);
  }, []);

  const handleOpenSubmitAccountConfirmation = useCallback((account) => {
    setAccountToSubmit(account);
    setShowSubmitAccountConfirmation(true);
  }, []);

  const handleOpenManagedAccounts = useCallback((account) => {
    const representative = {
      accountId: account.id,
      displayName: account.displayName || `${account.firstName} ${account.lastName}`.trim() || account.companyName
    };
    setSelectedManagedAccount(representative);
    setShowManagedAccountsModal(true);
  }, []);

  const handleOpenHistoricsDocuments = useCallback((account) => {
    setSelectedAccountForHistorics(account);
    setShowHistoricsModal(true);
  }, []);

  const handleCloseCashEditModal = useCallback(() => {
    setShowCashEditModal(false);
    setAccountToEditCash(null);
  }, []);

  const handleCloseDetailsModals = useCallback(() => {
    setShowViewMoreModal(false);
    setSelectedAccountForView(null);
  }, []);

  const handleCloseSignatoriesModal = useCallback(() => {
    setShowSignatoriesModal(false);
    setSelectedAccountForSignatories(null);
  }, []);

  const openContactForm = useCallback((accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    setSelectedAccount(account);
    setSelectedAccountId(accountId);
    setShowContactForm(true);
  }, [accounts]);

  const closeContactForm = useCallback(() => {
    setShowContactForm(false);
    setSelectedAccount(null);
    setSelectedAccountId(null);
  }, []);

  const openContactList = useCallback((accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    setSelectedAccount(account);
    setSelectedAccountId(accountId);
    setShowContactList(true);
  }, [accounts]);

  const closeContactList = useCallback(() => {
    setShowContactList(false);
    setSelectedAccountId(null);
  }, []);

  const openModal = useCallback((accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    setSelectedAccount(account);
    setSelectedAccountId(accountId);
    setIsModalOpen(true);
  }, [accounts]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedAccountId(null);
  }, []);

  const openAssociationForm = useCallback((accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    setSelectedAccount(account);
    setSelectedAccountId(accountId);
    setShowAssociationForm(true);
  }, [accounts]);

  const closeAssociationForm = useCallback(() => {
    setShowAssociationForm(false);
    setSelectedAccount(null);
    setSelectedAccountId(null);
  }, []);

  const openAssociationList = useCallback((accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    setSelectedAccount(account);
    setSelectedAccountId(accountId);
    setShowAssociationList(true);
  }, [accounts]);

  const closeAssociationList = useCallback(() => {
    setShowAssociationList(false);
    setSelectedAccount(null);
    setSelectedAccountId(null);
  }, []);

  const handleConfirmAccountSubmit = useCallback(async () => {
    if (!accountToSubmit) return;

    try {
      const userInfo = await getUserInfo();
      if (!userInfo) {
        toast.error("Impossible de récupérer l'utilisateur");
        return;
      }

      const success = await submitAccountValidation(accountToSubmit.id, userInfo.accountId);

      if (success) {
        refreshAccounts({ showLoading: false, silent: true });
      }

      setShowSubmitAccountConfirmation(false);
    } catch (error) {
      toast.error("Erreur lors de la soumission : " + error.message);
    }
  }, [accountToSubmit, getUserInfo, submitAccountValidation, refreshAccounts]);

  const handleValidationSuccess = useCallback(() => {
    refreshAccounts({ showLoading: false, silent: true });
  }, [refreshAccounts]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleRetry = useCallback(() => {
    refreshAccounts({ showLoading: true });
  }, [refreshAccounts]);

  /**
   * RENDU DU COMPOSANT
   */
  return (
    <div className="col-xl-12">
      {/*  Skeleton pour les filtres pendant le premier chargement */}
      {initialLoading ? (
        <SearchFiltersSkeleton />
      ) : (
        <SearchAndFilters 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleCashFilter={isConnectionError ? () => {} : handleCashFilter}
          cashFilter={cashFilter}
          cashFilterMessage={cashFilterMessage}
          handleAucFilter={handleAucFilter}
          aucFilter={aucFilter}
          aucFilterMessage={aucFilterMessage}
          resetFilters={resetFilters}
          handleAccountTypeFilter={handleAccountTypeFilter}
          accountTypeFilter={accountTypeFilter}
          handleStatusFilter={handleStatusFilter}
          statusFilter={statusFilter}
          handleManagedAccountFilter={handleManagedAccountFilter}
          isManagedAccountFilter={isManagedAccountFilter}
          isSearching={loading && !initialLoading}
          onRefresh={() => refreshAccounts({ showLoading: true, silent: false })}
        />
      )}

      {isConnectionError && (
        <div className="alert alert-offline">
          <div className="alert-content">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01"></path>
            </svg>
            <div>
              <strong>Mode hors ligne</strong>
              <p>Les données ne peuvent pas être chargées. Vérifiez votre connexion.</p>
            </div>
            <button onClick={handleRetry} className="btn btn-sm btn-outline-primary">
              Réessayer
            </button>
          </div>
        </div>
      )}

      <AccountsTable
        loading={loading && !isConnectionError}
        initialLoading={initialLoading} 
        error={error && !isConnectionError ? error : null}
        accounts={accounts}
        noResults={noResults}
        isOffline={isConnectionError}
        selectedDomain={selectedDomain}
        handleOpenSignatoriesModal={handleOpenSignatoriesModal}
        handleOpenDetailsModal={handleOpenDetailsModal}
        handleOpenValidationModal={handleOpenValidationModal}
        isCurrentUserValidator={isCurrentUserValidator}
        handleOpenModalOrders={handleOpenModalOrders}
        handleOpenCashEditModal={handleOpenCashEditModal}
        handleOpenModalSecurities={handleOpenModalSecurities}
        handleOpenModalTransactions={handleOpenModalTransactions}
        handleShowEditAccountModal={handleShowEditAccountModal}
        handleDeleteSuccess={handleDeleteSuccess}
        handleGenerateTitleReport={handleGenerateTitleReport}
        handleGenerateTransactionReport={handleGenerateTransactionReport}
        openContactForm={openContactForm}
        openContactList={openContactList}
        openModal={openModal}
        onRetry={handleRetry}
        handleOpenModal={handleOpenModal}
        openAssociationForm={openAssociationForm}
        openAssociationList={openAssociationList}
        handleOpenSubmitAccountConfirmation={handleOpenSubmitAccountConfirmation}
        handleOpenValidationDetails={handleOpenValidationDetails}
        searchTerm={debouncedSearchTerm}
        accountTypeFilter={accountTypeFilter}
        statusFilter={statusFilter}
        handleOpenManagedAccounts={handleOpenManagedAccounts}
        handleOpenHistoricsDocuments={handleOpenHistoricsDocuments}
      />

      {!isConnectionError && (
        <PaginationSection
          currentPage={currentPage}
          totalCount={totalCount}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setCurrentPage={setCurrentPage}
          handlePageChange={handlePageChange}
        />
      )}

      {/* MODALS - Tous inchangés pour économiser l'espace */}
      {showCashEditModal && accountToEditCash && (
        <CashEditForm 
          show={showCashEditModal} 
          onClose={handleCloseCashEditModal} 
          account={accountToEditCash} 
          onSuccess={() => refreshAccounts({ showLoading: false })} 
        />
      )}

      <ViewMoreModal 
        show={showViewMoreModal}
        onHide={handleCloseDetailsModals}
        account={selectedAccountForView}
      />

      {showSignatoriesModal && selectedAccountForSignatories && (
        <SignatoriesModal
          account={selectedAccountForSignatories}
          accountId={selectedAccountForSignatories.id}
          onClose={handleCloseSignatoriesModal}
        />
      )}

      {showSubmitAccountConfirmation && accountToSubmit && (
        <SubmitConfirmationModal
          message={`Soumettre le compte "${accountToSubmit.companyName || accountToSubmit.firstName + ' ' + accountToSubmit.lastName}" ?`}
          onConfirm={handleConfirmAccountSubmit}
          onCancel={() => setShowSubmitAccountConfirmation(false)}
          confirmText="Soumettre"
          cancelText="Annuler"
          confirmVariant="primary"
          isLoading={isSubmitting}
        />
      )}

      {showManagedAccountsModal && selectedManagedAccount && (
        <ManagedAccountsModal
          isOpen={showManagedAccountsModal}
          onClose={() => {
            setShowManagedAccountsModal(false);
            setSelectedManagedAccount(null);
          }}
          representative={selectedManagedAccount}
        />
      )}

      <ValidationModal
        show={showValidationModal}
        onClose={() => {
          setShowValidationModal(false);
          setValidationEntityId(null);
          setValidationEntityType('');
        }}
        entityId={validationEntityId}
        entityType={validationEntityType}
        onValidationSuccess={handleValidationSuccess}
      />

      {showValidationDetailModal && (
        <ValidationDetailModal
          entityId={selectedValidationEntityId}
          entityType={selectedValidationEntityType}
          isValidator={isCurrentUserValidator}
          onClose={() => setShowValidationDetailModal(false)}
          onApprove={() => {}}
          onReject={() => {}}
        />
      )}

      <CustomerForm
        initialAccountData={editAccountData}
        isEditMode={isEditMode}
        onCreateSuccess={handleCreateSuccess}
        onEditSuccess={handleEditSuccess}
        show={isEditMode}
        onHide={() => {
          setIsEditMode(false);
          setEditAccountData(null);
        }}
      />

      {isModalOpen && selectedAccount && (
        <div className="modal-overlay">
          <div className="modal-container">
            <AddLegalRepresentativesModal 
              account={selectedAccount} 
              accountId={selectedAccountId} 
              onClose={closeModal} 
              onSuccess={() => refreshAccounts({ showLoading: false })} 
              show={isModalOpen}
            />
          </div>
        </div>
      )}

      {showContactForm && selectedAccount && (
        <div className="modal-overlay">
          <div className="modal-container">
            <ContactForm 
              account={selectedAccount} 
              accountId={selectedAccountId} 
              onClose={closeContactForm} 
              onSuccess={() => refreshAccounts({ showLoading: false })}  
            />
          </div>
        </div>
      )}

      {showContactList && selectedAccount && (
        <div className="modal-overlay">
          <div className="modal-container">
            <TableauPersonne 
              account={selectedAccount} 
              accountId={selectedAccountId} 
              domainId={selectedDomain.id} 
              onClose={closeContactList} 
            />
          </div>
        </div>
      )}

      {isModalOpens && selectedAccount && (
        <div className="modal-overlay">
          <div className="modal-container">
            <TableauRepresentations 
              account={selectedAccount} 
              accountId={selectedAccountId} 
              domainId={selectedDomain.id} 
              onClose={() => setIsModalOpens(false)} 
            />
          </div>
        </div>
      )}

      {showAssociationForm && selectedAccount && (
        <div className="modal-overlay">
          <div className="modal-container">
            <AssociationForm 
              account={selectedAccount} 
              accountId={selectedAccountId} 
              onClose={closeAssociationForm} 
              onSuccess={() => refreshAccounts({ showLoading: false })}  
            />
          </div>
        </div>
      )}

      {showAssociationList && selectedAccount && (
        <div className="modal-overlay">
          <div className="modal-container">
            <TableauAssociation 
              account={selectedAccount} 
              accountId={selectedAccountId} 
              domainId={selectedDomain.id} 
              onClose={closeAssociationList} 
            />
          </div>
        </div>
      )}

      {showSecuritiesModal && selectedAccountId && (
        <div className="modal-overlay">
          <div className="modal-container">
            <ListeSecurities 
              issuerAccountId={selectedAccountId} 
              domainId={selectedDomain.id} 
              onClose={() => {
                setShowSecuritiesModal(false);
                setSelectedAccountId(null);
              }} 
            />
          </div>
        </div>
      )}

      {showTransactionModal && selectedAccountId && (
        <div className="modal-overlay">
          <div className="modal-container">
            <TransactionClient 
              fromAccountId={selectedAccountId} 
              domainId={selectedDomain.id} 
              onClose={() => {
                setShowTransactionModal(false);
                setSelectedAccountId(null);
              }} 
            />
          </div>
        </div>
      )}

      {showOrdersModal && selectedAccountId && (
        <div className="modal-overlay">
          <div className="modal-container">
            <ListeOrders 
              fromAccountId={selectedAccountId} 
              domainId={selectedDomain.id} 
              onClose={() => {
                setShowOrdersModal(false);
                setSelectedAccountId(null);
              }} 
            />
          </div>
        </div>
      )}

      {showReportPeriodModal && selectedReportAccount && (
        <ReportPeriodModal
          show={showReportPeriodModal}
          onClose={() => {
            setShowReportPeriodModal(false);
            setSelectedReportAccount(null);
            setSelectedReportDomainId(null);
          }}
          onGenerate={handleGenerateReportWithPeriod}
          accountName={selectedReportAccount.displayName || 
                      selectedReportAccount.companyName || 
                      `${selectedReportAccount.firstName} ${selectedReportAccount.lastName}`}
        />
      )}

      {showHistoricsModal && selectedAccountForHistorics && (
        <AccountHistoricsModal
          show={showHistoricsModal}
          onHide={() => {
            setShowHistoricsModal(false);
            setSelectedAccountForHistorics(null);
          }}
          account={selectedAccountForHistorics}
          domainId={selectedDomain?.id}
        />
      )}

      {isGeneratingReport && (
        <div className="text-center my-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Génération en cours...</span>
          </div>
          <p className="mt-2 text-muted">Génération du rapport...</p>
        </div>
      )}
    </div>
  );
};

export default Accounts;